import "../../IssuancePanel.css";
import "./IssuancePanelDynamic.css";

import { Dispatch, FormEvent, SetStateAction } from "react";

import { ProjectIssuanceConstants } from "../../../../constants";
import {
  PillData,
  ProjectIssuanceSpecificProperty,
  ProjectIssuanceSpecificSchema,
  SelectData,
} from "../../../../models";
import { roundToDecimalPlaces } from "../../../../utils";
import {
  ActionButtons,
  Column,
  DatePicker,
  DynamicFormFieldsFromJsonSchema,
  FormLabel,
  KanaTooltip,
  PillsList,
  ReadonlyText,
  Row,
  Select,
  TextInput,
} from "../../../../widget";
import { isUnitKmHabitatType } from "../../utils";
import {
  getConditionalEnumValues,
  getIssuanceFieldValue,
  isReadOnlyOrConstField,
  IssuancePanelData,
  normaliseFieldLabel,
  setIssuanceFieldValue,
  shouldRenderSchemaField,
} from "./utils";

interface IssuancePanelDynamicProps {
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined;

  issuanceData: IssuancePanelData;
  setIssuanceData: Dispatch<SetStateAction<IssuancePanelData>>;

  tags: PillData[];
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;

  isHandleSubmitLoading: boolean;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleClosePanel: () => void;
  verifierData: SelectData;
}

export const IssuancePanelDynamic = ({
  issuanceSchema,
  issuanceData,
  setIssuanceData,
  tags,
  setShowTagsPanel,
  isHandleSubmitLoading,
  handleSubmit,
  handleClosePanel,
  verifierData,
}: IssuancePanelDynamicProps): JSX.Element => {
  const schemaProperties = issuanceSchema?.properties ?? {};

  const quantityTypeDefinition = schemaProperties.quantityType;
  const shouldRenderQuantityTypeField = !quantityTypeDefinition || !isReadOnlyOrConstField(quantityTypeDefinition);
  const quantityTypeOptions =
    shouldRenderQuantityTypeField && quantityTypeDefinition?.enum && quantityTypeDefinition.enum.length > 0
      ? quantityTypeDefinition.enum.map((optionValue) => ({ key: optionValue, value: optionValue }))
      : [];

  const getFieldValue = (fieldKey: string): unknown => {
    return getIssuanceFieldValue(issuanceData, fieldKey);
  };

  const setFieldValue = (fieldKey: string, value: unknown): void => {
    setIssuanceFieldValue(setIssuanceData, fieldKey, value);
    if (fieldKey === "habitatType") {
      setIssuanceFieldValue(setIssuanceData, "habitat", null);
      if (isUnitKmHabitatType(value as string)) {
        setIssuanceFieldValue(setIssuanceData, "unitKm", getFieldValue("unitHa"));
        setIssuanceFieldValue(setIssuanceData, "unitHa", null);
      } else {
        setIssuanceFieldValue(setIssuanceData, "unitHa", getFieldValue("unitKm"));
        setIssuanceFieldValue(setIssuanceData, "unitKm", null);
      }
    }
  };

  const shouldRenderDynamicField = (fieldKey: string, fieldDefinition: ProjectIssuanceSpecificProperty): boolean => {
    if (!shouldRenderSchemaField(fieldKey, fieldDefinition)) {
      return false;
    }

    if (fieldKey !== "unitKm" && fieldKey !== "unitHa") {
      return true;
    }

    const habitatTypeValue = getFieldValue("habitatType");
    const habitatType = typeof habitatTypeValue === "string" ? habitatTypeValue : null;
    const shouldShowUnitKm = isUnitKmHabitatType(habitatType);

    return fieldKey === "unitKm" ? shouldShowUnitKm : !shouldShowUnitKm;
  };

  const isDynamicFieldReadonly = (fieldKey: string): boolean => {
    if (fieldKey !== "habitat") {
      return false;
    }

    const habitatGroupValue = getFieldValue("habitatType");
    if (habitatGroupValue == null) {
      return true;
    }

    return String(habitatGroupValue).trim() === "";
  };

  const renderVerifierField = (): JSX.Element => {
    if (verifierData && verifierData.length > 0) {
      return (
        <Select
          label="Validation & verification body (VVB)"
          data={verifierData}
          value={
            verifierData.some((element) => element.key === issuanceData.verifierUuid)
              ? issuanceData.verifierUuid
              : undefined
          }
          onChange={(verifierUuidNewValue) =>
            setIssuanceData({
              ...issuanceData,
              verifierUuid: verifierUuidNewValue,
            })
          }
        />
      );
    }

    return (
      <>
        <Row spacingV="m">
          <Column span={12}>
            <h3>Validation & verification body (VVB)</h3>
          </Column>
        </Row>
        <Row>
          <Column span={12}>
            <p className="body1 NoData">No validators available for this project</p>
          </Column>
        </Row>
      </>
    );
  };

  return (
    <div className="IssuancePanelContent">
      <form onSubmit={(e) => handleSubmit(e)}>
        <Row spacingV="l">
          <Column span={4}>
            <Select
              label="Issuance status"
              onChange={(value: string) =>
                setIssuanceData({
                  ...issuanceData,
                  status: value,
                })
              }
              value={issuanceData.status}
              data={ProjectIssuanceConstants.ISSUANCE_STATUS_SELECT_DATA}
              tooltip={
                <KanaTooltip
                  tooltipHeader=""
                  tooltipText="Setting this to 'Draft' allows you to model projected unit stock before it is issued. 'Draft' issuances will not be displayed on the public directory."
                />
              }
            />
          </Column>
          <Column offset="1_half" span={4}>
            <DatePicker
              label="Issuance date"
              onChange={(value) =>
                setIssuanceData({
                  ...issuanceData,
                  issuanceDate: value ?? null,
                })
              }
              value={issuanceData.issuanceDate ?? undefined}
              showFormat={false}
            />
          </Column>
        </Row>

        <Row spacingV="l">
          <Column span={4}>
            <TextInput
              label="Price"
              onChange={(value) =>
                setIssuanceData({
                  ...issuanceData,
                  price: value === "" ? null : Number(value),
                })
              }
              value={issuanceData.price?.toString()}
              name="price"
              type="number"
              minNumberValue={0}
              maxNumberValue={999999.99}
              prefix="£"
            />
          </Column>
          <Column offset="1_half" span={4}>
            <TextInput
              label="Available units"
              onChange={(value: string) =>
                setIssuanceData({
                  ...issuanceData,
                  availableQuantity: Number(value),
                })
              }
              value={issuanceData.availableQuantity?.toString()}
              name="availableUnits"
              type="number"
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
            />
          </Column>
        </Row>

        <Row spacingV="l">
          <Column span={4}>
            <TextInput
              label="Sold units"
              onChange={(value: string) =>
                setIssuanceData({
                  ...issuanceData,
                  soldQuantity: Number(value),
                })
              }
              value={issuanceData.soldQuantity?.toString()}
              name="soldUnits"
              type="number"
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
            />
          </Column>
          <Column offset="1_half" span={4}>
            <TextInput
              label="Reserved units"
              onChange={(value: string) =>
                setIssuanceData({
                  ...issuanceData,
                  reservedQuantity: Number(value),
                })
              }
              value={issuanceData.reservedQuantity?.toString()}
              name="reservedUnits"
              type="number"
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
            />
          </Column>
        </Row>

        <Row spacingV="l">
          <Column span={4}>
            <ReadonlyText
              label="Total units"
              value={
                issuanceData.soldQuantity != null ||
                issuanceData.reservedQuantity != null ||
                issuanceData.availableQuantity != null
                  ? roundToDecimalPlaces(
                      (issuanceData.soldQuantity ?? 0) +
                        (issuanceData.reservedQuantity ?? 0) +
                        (issuanceData.availableQuantity ?? 0),
                      5
                    )
                  : "--"
              }
            />
          </Column>
          <Column offset="1_half" span={4}>
            <TextInput
              label="Serial number"
              onChange={(value) =>
                setIssuanceData({
                  ...issuanceData,
                  serialNumber: value,
                })
              }
              value={issuanceData.serialNumber}
              name="serialNumber"
            />
          </Column>
        </Row>

        <Row spacingV="l">
          <Column span={4}>{renderVerifierField()}</Column>
        </Row>

        <DynamicFormFieldsFromJsonSchema
          schemaProperties={schemaProperties}
          getFieldValue={getFieldValue}
          onFieldChange={setFieldValue}
          shouldRenderField={shouldRenderDynamicField}
          isFieldReadonly={isDynamicFieldReadonly}
          getFieldLabel={(fieldKey) => normaliseFieldLabel(fieldKey)}
          getEnumValues={(fieldKey, fieldDefinition, getSchemaFieldValue) =>
            fieldDefinition.enum ?? getConditionalEnumValues(issuanceSchema, fieldKey, getSchemaFieldValue)
          }
          valueMappers={{
            text: ({ value }) => (value === "" ? null : value),
            number: ({ value }) => {
              if (value === "") {
                return null;
              }

              const parsedValue = Number(value);
              return Number.isNaN(parsedValue) ? null : parsedValue;
            },
            date: ({ value }) => value ?? null,
          }}
          getNumberInputProps={() => ({ minNumberValue: 0 })}
          renderLayout={(fields) => {
            const { columns: fieldColumns, columnSlot } = fields.reduce<{
              columns: JSX.Element[];
              columnSlot: number;
            }>(
              (acc, field) => {
                if (field.isFullWidth) {
                  acc.columns.push(
                    <Column span={12} key={field.key} className="IssuancePanelDynamicJsonField">
                      <div>{field.element}</div>
                    </Column>
                  );
                  acc.columnSlot = 0;
                } else {
                  acc.columns.push(
                    <Column
                      span={4}
                      offset={acc.columnSlot % 2 ? "1_half" : undefined}
                      key={field.key}
                      className="IssuancePanelDynamicJsonField"
                    >
                      <div>{field.element}</div>
                    </Column>
                  );
                  acc.columnSlot += 1;
                }
                return acc;
              },
              { columns: [], columnSlot: 0 }
            );

            return (
              <Row spacingV="ml">
                {fieldColumns}
                <Column
                  span={4}
                  offset={columnSlot % 2 ? "1_half" : undefined}
                  className="IssuancePanelDynamicJsonField"
                >
                  <Select
                    label="Unit type"
                    onChange={(value: string) =>
                      setIssuanceData({
                        ...issuanceData,
                        quantityType: value,
                      })
                    }
                    value={issuanceData.quantityType}
                    data={quantityTypeOptions}
                  />
                </Column>
                <Column
                  span={4}
                  offset={(columnSlot + 1) % 2 ? "1_half" : undefined}
                  className="IssuancePanelDynamicJsonField"
                >
                  <FormLabel label="Tags" />
                  <PillsList pills={tags} linkText="+ Add a tag" linkOnClick={() => setShowTagsPanel(true)} />
                </Column>
              </Row>
            );
          }}
        />

        <Row spacingV="4xl">
          <Column span={12}>
            <ActionButtons
              submitButtonLabel="Save changes"
              onCancel={handleClosePanel}
              isLoading={isHandleSubmitLoading}
            />
          </Column>
        </Row>
      </form>
    </div>
  );
};
